import { answerText, isCorrect } from "./answers.js";
/**
 * THE FINAL FEW — the rules engine.
 *
 * `apply(state, event)` is the whole game: pure, with no timers, network or
 * randomness inside. Commands (openQuestion, lockAnswer, pass…) check a move
 * against the rules and return the events it produces, or a refusal. The room
 * on the server owns the clock and the question draw; it passes `now` in.
 * Replaying the event log through `apply` rebuilds any game exactly.
 */
// ---------- The rules, as numbers ----------
/** The fifteen rungs: the share of the public who get each question right. */
export const LADDER = [90, 80, 70, 60, 50, 45, 40, 35, 30, 25, 20, 15, 10, 5, 1];
export const RULES = {
    seats: 100,
    stake: 1000,
    /** Finalists who take the safe option share this. */
    shareFund: 10_000,
    rolloverCap: 500_000,
    /** The pass can be used on questions 5 to 14. */
    passFrom: 5,
    passTo: 14,
    /** The walk-away offer comes before this question. */
    walkBefore: 9,
    /** With one player left before this question, it and the next are played for fun. */
    forFunFrom: 13,
    answerMs: 30_000,
    relaxedAnswerMs: 45_000,
    /** Phones lag: an answer this soon after the deadline still counts. */
    graceMs: 250,
};
export const FINAL_Q = LADDER.length;
export const LAST_NORMAL_Q = FINAL_Q - 1;
const ok = (...events) => ({ ok: true, events });
const no = (code, message) => ({ ok: false, code, message });
export class RuleError extends Error {
}
// ---------- The reducer ----------
export function emptyState() {
    return {
        seed: 0,
        mode: "standard",
        shareRule: "takers",
        seats: [],
        stage: "lobby",
        q: 0,
        question: null,
        readEndsAt: null,
        deadline: null,
        locked: {},
        forFun: false,
        pot: 0,
        rolloverIn: 0,
        results: null,
        fallbackFinal: false,
        walkChoices: {},
        walkOffered: false,
        settlement: null,
        seq: 0,
    };
}
const updateSeat = (seats, n, patch) => seats.map((s) => (s.seat === n ? { ...s, ...patch } : s));
export function apply(state, event) {
    const next = { ...state, seq: state.seq + 1 };
    switch (event.type) {
        case "GameStarted":
            return {
                ...emptyState(),
                seq: next.seq,
                seed: event.seed,
                mode: event.mode,
                shareRule: event.shareRule,
                rolloverIn: event.rolloverIn,
                pot: event.rolloverIn,
                seats: event.seats.map((s, i) => ({
                    seat: i,
                    kind: s.kind,
                    playerId: s.playerId,
                    name: s.name,
                    featured: s.featured,
                    state: "active",
                    passQ: null,
                    outQ: null,
                    stakeHeld: true,
                    finalChoice: null,
                    winnings: 0,
                })),
            };
        case "QuestionOpened":
            return {
                ...next,
                stage: "question",
                q: event.q,
                question: event.question,
                readEndsAt: event.readEndsAt,
                deadline: event.deadline,
                locked: {},
                forFun: event.forFun,
                results: null,
            };
        case "AnswerLocked":
            return { ...next, locked: { ...state.locked, [event.seat]: { value: event.value, passed: false, at: event.at } } };
        case "Passed":
            return {
                ...next,
                locked: { ...state.locked, [event.seat]: { value: null, passed: true, at: event.at } },
                seats: updateSeat(state.seats, event.seat, { passQ: state.q, stakeHeld: false }),
                pot: state.pot + RULES.stake,
            };
        case "QuestionClosed": {
            let pot = state.pot;
            const correct = new Map(event.results.map((r) => [r.seat, r.correct]));
            const seats = state.seats.map((s) => {
                const playing = s.state === "active" || (state.q === FINAL_Q && s.state === "finalist" && s.finalChoice === "play");
                if (!playing || state.q === FINAL_Q)
                    return s;
                if (state.locked[s.seat]?.passed || correct.get(s.seat) || state.forFun)
                    return s;
                // Out. Their stake joins the pot, unless the pass already put it there.
                if (s.stakeHeld)
                    pot += RULES.stake;
                return { ...s, state: "eliminated", outQ: state.q, stakeHeld: false };
            });
            return {
                ...next,
                stage: "reveal",
                seats,
                pot,
                deadline: null,
                results: event.results.map((r) => ({
                    seat: r.seat,
                    correct: r.correct,
                    passed: !!state.locked[r.seat]?.passed,
                    value: state.locked[r.seat]?.value ?? null,
                })),
            };
        }
        case "FinalistsDeclared": {
            const chosen = new Set(event.seats);
            return {
                ...next,
                fallbackFinal: event.fallback,
                seats: state.seats.map((s) => chosen.has(s.seat) ? { ...s, state: "finalist", finalChoice: event.fallback ? "play" : null } : s),
            };
        }
        case "WalkOfferOpened":
            return { ...next, stage: "walk-offer", deadline: event.deadline, walkChoices: {}, walkOffered: true };
        case "WalkChosen":
            return { ...next, walkChoices: { ...state.walkChoices, [event.seat]: event.walk } };
        case "WalkOfferClosed":
            return {
                ...next,
                stage: "reveal",
                deadline: null,
                // Walkers keep their £1,000; nothing moves to the pot.
                seats: state.seats.map((s) => (state.walkChoices[s.seat] && s.state === "active" ? { ...s, state: "walked" } : s)),
            };
        case "FinalOfferOpened":
            return { ...next, stage: "final-offer", deadline: event.deadline };
        case "FinalChoiceMade":
            return { ...next, seats: updateSeat(state.seats, event.seat, { finalChoice: event.choice }) };
        case "FinalChoicesRevealed": {
            const defaults = new Set(event.defaults);
            return {
                ...next,
                stage: "reveal",
                deadline: null,
                seats: state.seats.map((s) => (defaults.has(s.seat) ? { ...s, finalChoice: "share" } : s)),
            };
        }
        case "GameSettled":
            return {
                ...next,
                stage: "over",
                settlement: event.settlement,
                seats: state.seats.map((s) => ({
                    ...s,
                    winnings: event.winnings[s.seat] ?? s.winnings,
                    state: event.states[s.seat] ?? s.state,
                })),
            };
    }
}
export function replay(events) {
    return events.reduce(apply, emptyState());
}
// ---------- Questions of state ----------
export const seatsIn = (state, ...states) => state.seats.filter((s) => states.includes(s.state));
export const activeSeats = (state) => seatsIn(state, "active");
export const finalists = (state) => seatsIn(state, "finalist", "took_share", "won", "lost");
/** Who must answer the open question. */
export function answering(state) {
    if (state.q === FINAL_Q)
        return state.seats.filter((s) => s.state === "finalist" && s.finalChoice === "play");
    return activeSeats(state);
}
export function canPass(state, seat) {
    return (state.stage === "question" &&
        !state.forFun &&
        state.q >= RULES.passFrom &&
        state.q <= RULES.passTo &&
        seat.state === "active" &&
        seat.passQ === null &&
        !(seat.seat in state.locked));
}
export const canWalk = (state, seat) => state.stage === "walk-offer" && seat.state === "active" && seat.passQ === null;
export function start(state, input) {
    if (state.stage !== "lobby" || state.seats.length > 0)
        return no("already-started", "The game has already started.");
    if (input.seats.length !== RULES.seats)
        return no("seats", `A game needs exactly ${RULES.seats} seats.`);
    const mode = input.mode ?? "standard";
    // The pot can never pass the cap: a carried jackpot leaves room for this game's stakes.
    const roomForStakes = RULES.rolloverCap - RULES.seats * RULES.stake;
    const rolloverIn = mode === "rollover" ? Math.max(0, Math.min(roomForStakes, Math.floor(input.rolloverIn ?? 0))) : 0;
    return ok({
        type: "GameStarted",
        seed: input.seed,
        mode,
        shareRule: input.shareRule ?? "takers",
        rolloverIn,
        seats: input.seats.map((s) => ({ kind: s.kind, playerId: s.playerId, name: s.name, featured: s.featured ?? null })),
    });
}
/** Open the next question. `readMs` is how long the host takes to read it; the clock starts after. */
export function openQuestion(state, question, now, readMs) {
    // Once there are finalists, the next question is the final, whichever rung the ladder reached.
    const q = state.seats.some((s) => s.state === "finalist") ? FINAL_Q : state.q + 1;
    if (state.q >= FINAL_Q)
        return no("over", "There are no more questions.");
    if (q === 1 ? state.stage !== "lobby" || state.seats.length === 0 : state.stage !== "reveal")
        return no("stage", "Not yet.");
    if (q === FINAL_Q) {
        if (finalists(state).length === 0)
            return no("no-finalists", "Nobody reached the final.");
        if (!state.seats.some((s) => s.state === "finalist" && s.finalChoice === "play"))
            return no("nobody-playing", "Nobody chose to play.");
    }
    else {
        if (activeSeats(state).length === 0 || finalists(state).length > 0)
            return no("stage", "The ladder is over.");
        if (nextStep(state).kind === "walk-offer")
            return no("walk-first", "The walk-away offer comes first.");
    }
    const readEndsAt = now + Math.max(0, readMs);
    const answerMs = state.mode === "relaxed" ? RULES.relaxedAnswerMs : RULES.answerMs;
    // One player left before the 10% question: it and the 5% are played for fun.
    const forFun = state.forFun || (q === RULES.forFunFrom && activeSeats(state).length === 1);
    return ok({ type: "QuestionOpened", q, question, readEndsAt, deadline: readEndsAt + answerMs, forFun: q < FINAL_Q && forFun });
}
function inTime(state, now) {
    return state.deadline !== null && now <= state.deadline + RULES.graceMs;
}
export function lockAnswer(state, seatNo, value, now) {
    if (state.stage !== "question")
        return no("stage", "There's no question open.");
    const seat = state.seats[seatNo];
    if (!seat || !answering(state).some((s) => s.seat === seatNo))
        return no("not-playing", "You're not answering this one.");
    if (seatNo in state.locked)
        return no("locked", "Your answer is locked in.");
    if (!inTime(state, now))
        return no("late", "Time's up.");
    const v = value.trim().slice(0, 60);
    if (!v)
        return no("empty", "Give an answer first.");
    return ok({ type: "AnswerLocked", seat: seatNo, value: v, at: now });
}
export function pass(state, seatNo, now) {
    const seat = state.seats[seatNo];
    if (!seat)
        return no("seat", "No such seat.");
    if (state.stage !== "question")
        return no("stage", "There's no question open.");
    if (seat.passQ !== null)
        return no("passed", "You've already used your pass.");
    if (seatNo in state.locked)
        return no("locked", "Your answer is locked in.");
    if (state.q < RULES.passFrom || state.q > RULES.passTo || state.forFun)
        return no("no-pass", "You can't pass on this question.");
    if (seat.state !== "active")
        return no("not-playing", "You're not answering this one.");
    if (!inTime(state, now))
        return no("late", "Time's up.");
    return ok({ type: "Passed", seat: seatNo, at: now });
}
/** Score the open question. Early only once everyone answering has locked in. */
export function closeQuestion(state, now) {
    if (state.stage !== "question" || !state.question)
        return no("stage", "There's no question open.");
    const players = answering(state);
    const allIn = players.every((s) => s.seat in state.locked);
    if (!allIn && state.deadline !== null && now <= state.deadline + RULES.graceMs)
        return no("early", "Answers are still coming in.");
    const key = state.question;
    const results = players.map((s) => {
        const l = state.locked[s.seat];
        return { seat: s.seat, correct: !!l && !l.passed && isCorrect(key, l.value) };
    });
    const events = [{ type: "QuestionClosed", q: state.q, results }];
    if (state.q === FINAL_Q)
        return { ok: true, events: [...events, settleEvent(apply(state, events[0]))] };
    const after = apply(state, events[0]);
    const left = activeSeats(after);
    if (left.length === 0) {
        // Everyone out: those who fell on this question play the final, for the pot only.
        const fell = after.seats.filter((s) => s.outQ === state.q).map((s) => s.seat);
        if (fell.length > 0)
            events.push({ type: "FinalistsDeclared", seats: fell, fallback: true });
    }
    else if (state.q === LAST_NORMAL_Q) {
        events.push({ type: "FinalistsDeclared", seats: left.map((s) => s.seat), fallback: false });
    }
    return { ok: true, events };
}
// ---------- The walk-away offer ----------
export function openWalkOffer(state, now, ms) {
    if (state.stage !== "reveal" || state.q !== RULES.walkBefore - 1)
        return no("stage", "The offer comes before the 30% question.");
    if (state.walkOffered)
        return no("done", "The offer has been made.");
    return ok({ type: "WalkOfferOpened", deadline: now + ms });
}
export function chooseWalk(state, seatNo, walk, now) {
    const seat = state.seats[seatNo];
    if (!seat || !canWalk(state, seat))
        return no("not-offered", "This offer isn't for you.");
    if (!inTime(state, now))
        return no("late", "Time's up.");
    if (state.walkChoices[seatNo] === walk)
        return ok();
    return ok({ type: "WalkChosen", seat: seatNo, walk });
}
export function closeWalkOffer(state) {
    if (state.stage !== "walk-offer")
        return no("stage", "There's no offer open.");
    return ok({ type: "WalkOfferClosed" });
}
// ---------- The final ----------
export function openFinalOffer(state, now, ms) {
    if (state.stage !== "reveal" || state.fallbackFinal || !state.seats.some((s) => s.state === "finalist" && s.finalChoice === null)) {
        return no("stage", "There's no offer to make.");
    }
    return ok({ type: "FinalOfferOpened", deadline: now + ms });
}
export function chooseFinal(state, seatNo, choice, now) {
    const seat = state.seats[seatNo];
    if (state.stage !== "final-offer")
        return no("stage", "There's no offer open.");
    if (!seat || seat.state !== "finalist")
        return no("not-finalist", "Only finalists choose.");
    if (!inTime(state, now))
        return no("late", "Time's up.");
    if (seat.finalChoice === choice)
        return ok();
    return ok({ type: "FinalChoiceMade", seat: seatNo, choice });
}
/** Reveal everyone's choice at once. Anyone who didn't choose takes the share. */
export function revealFinalChoices(state) {
    if (state.stage !== "final-offer")
        return no("stage", "There's no offer open.");
    const defaults = state.seats.filter((s) => s.state === "finalist" && s.finalChoice === null).map((s) => s.seat);
    const events = [{ type: "FinalChoicesRevealed", defaults }];
    const after = apply(state, events[0]);
    // Nobody plays: settle without the last question.
    if (!after.seats.some((s) => s.state === "finalist" && s.finalChoice === "play"))
        events.push(settleEvent(after));
    return { ok: true, events };
}
/** Settle a game with no finalists at all (everyone walked, or the ladder emptied). */
export function endWithoutFinal(state) {
    if (state.stage !== "reveal" || finalists(state).length > 0 || activeSeats(state).length > 0)
        return no("stage", "The game isn't over.");
    return ok(settleEvent(state));
}
function settleEvent(state) {
    const fin = state.seats.filter((s) => s.state === "finalist");
    const takers = fin.filter((s) => s.finalChoice === "share");
    const players = fin.filter((s) => s.finalChoice === "play");
    const right = state.q === FINAL_Q && state.results ? new Set(state.results.filter((r) => r.correct).map((r) => r.seat)) : new Set();
    const winners = players.filter((s) => right.has(s.seat)).map((s) => s.seat);
    const splitAcross = state.shareRule === "all" ? fin.length : takers.length;
    const shareEach = takers.length > 0 ? Math.floor(RULES.shareFund / splitAcross) : 0;
    const potEach = winners.length > 0 ? Math.floor(state.pot / winners.length) : 0;
    const remainder = winners.length > 0 ? state.pot - potEach * winners.length : 0;
    const unwon = winners.length > 0 ? 0 : state.pot;
    const rolloverOut = state.mode === "rollover" ? Math.min(RULES.rolloverCap, unwon + remainder) : 0;
    const winnings = {};
    const states = {};
    for (const s of state.seats) {
        // Anyone still holding their £1,000 keeps it: walkers, and finalists who never passed or fell.
        const held = s.stakeHeld && (s.state === "walked" || s.state === "finalist") ? RULES.stake : 0;
        if (s.state !== "finalist") {
            winnings[s.seat] = held;
            continue;
        }
        if (s.finalChoice === "share") {
            winnings[s.seat] = shareEach + held;
            states[s.seat] = "took_share";
        }
        else if (winners.includes(s.seat)) {
            winnings[s.seat] = potEach + held;
            states[s.seat] = "won";
        }
        else {
            winnings[s.seat] = held;
            states[s.seat] = "lost";
        }
    }
    return { type: "GameSettled", settlement: { shareEach, potEach, winners, unwon, remainder, rolloverOut }, winnings, states };
}
/** After a reveal (or at the start), what the room should do next. */
export function nextStep(state) {
    if (state.stage === "over")
        return { kind: "over" };
    if (state.stage === "lobby")
        return state.seats.length > 0 ? { kind: "question", q: 1 } : { kind: "wait" };
    if (state.stage !== "reveal")
        return { kind: "wait" };
    const fin = state.seats.filter((s) => s.state === "finalist");
    if (state.q === FINAL_Q)
        return { kind: "over" };
    if (fin.length > 0) {
        if (fin.some((s) => s.finalChoice === null))
            return { kind: "final-offer" };
        return fin.some((s) => s.finalChoice === "play") ? { kind: "final-question" } : { kind: "settle" };
    }
    if (activeSeats(state).length === 0)
        return { kind: "settle" };
    if (state.q === RULES.walkBefore - 1 && !state.walkOffered) {
        const eligible = activeSeats(state).some((s) => s.passQ === null);
        if (eligible)
            return { kind: "walk-offer" };
    }
    return { kind: "question", q: state.q + 1 };
}
// ---------- Invariants ----------
/** Problems with a state, or [] if it's sound. Checked after every event in tests. */
export function invariantProblems(state) {
    const problems = [];
    if (state.seats.length === 0)
        return problems;
    if (state.seats.length !== RULES.seats)
        problems.push("wrong number of seats");
    if (state.stage !== "over") {
        const given = state.seats.filter((s) => !s.stakeHeld).length * RULES.stake;
        if (state.pot !== state.rolloverIn + given)
            problems.push(`stakes don't add up: pot ${state.pot}, given ${given}`);
    }
    if (state.pot > RULES.rolloverCap)
        problems.push("pot over the cap");
    if (state.mode !== "rollover" && state.pot > RULES.seats * RULES.stake)
        problems.push("pot over £100,000");
    for (const s of state.seats) {
        if (s.passQ !== null && (s.passQ < RULES.passFrom || s.passQ > RULES.passTo))
            problems.push(`seat ${s.seat} passed on Q${s.passQ}`);
        if (s.state === "eliminated" && s.outQ === null)
            problems.push(`seat ${s.seat} out with no question`);
        if (s.state === "walked" && s.passQ !== null)
            problems.push(`seat ${s.seat} walked after passing`);
        if (s.state === "walked" && !s.stakeHeld)
            problems.push(`seat ${s.seat} walked without their stake`);
    }
    return problems;
}
// ---------- Reveal text ----------
export function revealText(question) {
    return answerText(question);
}
//# sourceMappingURL=engine.js.map